/**
 * The agent's one user-changeable setting — the host command timeout —
 * `GET|PUT /settings/timeout` on the local agent (item BH5). Ported from
 * `bimgo-ai-web`'s `useAgentTimeout` request half; the hook lives in the card.
 */
import { agentClient, type AgentClient } from "./agentClient";
import { parseAskError } from "./askError";
import { bearer } from "./accessToken";

/** The agent's payload, field for field. */
export interface AgentTimeoutSettings {
  baseTimeoutMs: number;
  safetyBufferMs: number;
  outerTimeoutMs: number;
  defaultBaseTimeoutMs: number;
  source: "settings" | "default";
  options: number[];
}

export const TIMEOUT_ENDPOINT = "/settings/timeout";

async function request(
  method: "GET" | "PUT",
  token: string,
  body: unknown,
  client: AgentClient,
): Promise<AgentTimeoutSettings> {
  const response = await client.fetch(TIMEOUT_ENDPOINT, {
    method,
    headers: {
      Authorization: bearer(token),
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const parsed = parseAskError(response.status, payload, response.statusText);
    throw new Error(parsed.message || `HTTP ${response.status}`);
  }
  return payload as AgentTimeoutSettings;
}

export function getAgentTimeout(token: string, client: AgentClient = agentClient): Promise<AgentTimeoutSettings> {
  return request("GET", token, undefined, client);
}

export function setAgentTimeout(
  token: string,
  baseTimeoutMs: number,
  client: AgentClient = agentClient,
): Promise<AgentTimeoutSettings> {
  return request("PUT", token, { baseTimeoutMs }, client);
}

/** "300 s", or "300 s (default)" for the agent's own default. */
export function formatTimeoutOption(
  ms: number,
  defaultMs: number,
  t: (key: string) => string,
): string {
  const label = t("settings.user.timeout.seconds").replace("{seconds}", String(Math.round(ms / 1000)));
  return ms === defaultMs ? `${label} (${t("settings.user.timeout.default")})` : label;
}
